import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getErrorMessages } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList, { AddToWishListParameter } from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
// import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@tSurya/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@tSurya/Overrides/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import BrandBadge from "@tSurya/Components/BrandBadge";
import {
    MGBW_IS_UNCONFIGURED,
    MGBW_CONFIGURATION_ITEM_NUMBER,
    MGBW_SHIPPING_AVAILABILITY,
    MGBW_VALIDATION_STATUS,
    MGBW_VALIDATION_MESSAGES,
    MGBW_UPDATE_CART_LINE_ID,
    MGBW_UPDATE_QTY,
    MgbwValidationStatus,
} from "@tSurya/Common/MgbwPropertyKeys";
import { useMgbwSelections, useVisibleMgbwSelections } from "@tSurya/Common/Hooks/useMgbwSelections";
import { getMgbwSelectionsRecord } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { buildCrossWebsiteProductDetailPath } from "@tSurya/Common/Utilities/websiteUtils";
import removeCartLine from "@tSurya/Handlers/Cart/RemoveCartLineExtended";
import { appendSelectionsToUrl } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import TrashIcon from "@tSurya/Icons/TrashIcon";
import CustomQuantityInput from "@tSurya/Overrides/Components/CustomQuantityInput";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design.surya";
import { mgbwConfiguratorLinkStyles } from "@tSurya/theme/mixins/link";
import EditIcon from "@tSurya/Icons/EditIcon";
import React, { FC, useContext, useMemo, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    suryaWebsiteUrl: (state.context.settings.properties.suryaWebsiteUrl as string) || "https://www.surya.com",
    globalViewsWebsiteUrl: (state.context.settings.properties.globalViewsWebsiteUrl as string) || "https://globalviews.surya.com",
    mgbwWebsiteUrl: (state.context.settings.properties.mgbwWebsiteUrl as string) || "https://mgbw.surya.com",
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    rowItems?: InjectableCss;
    secondRowItems?: InjectableCss;
    desktopItemsContainer?: InjectableCss;
    mobileRowItems?: InjectableCss;
    mobileItemsContainer?: InjectableCss;
    thirdRowItems?: InjectableCss;
    mobileThirdRowItems?: InjectableCss;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    firstRowItem?: InjectableCss;
    brandAndDescriptionContainer?: InjectableCss;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    configurationDetails?: InjectableCss;
    configurationSummary?: TypographyProps;
    configurationLabel?: TypographyProps;
    mgbwShippingAvailabilityText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    mobileQuantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    customQuantityInput?: any;
    extendedUnitNetPriceGridItem?: GridItemProps;
    priceLabelText?: TypographyPresentationProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    editCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;
    cartLineServiceGridItem?: GridItemProps;
    serviceLabel?: InjectableCss;
    modalContainer?: ModalPresentationProps;
    modalBodyContainer?: GridContainerProps;
    closeButton?: ButtonPresentationProps;
    modalContent?: TypographyPresentationProps;
    modalYesButton?: ButtonPresentationProps;
    columnContainer?: InjectableCss;
    imageColumn?: InjectableCss;
    productColumn?: InjectableCss;
    unitPriceColumn?: InjectableCss;
    qtyColumn?: InjectableCss;
    totalPriceColumn?: InjectableCss;
    deleteColumn?: InjectableCss;
    productDetails?: InjectableCss;
    mobileLineItemBottomContainer?: InjectableCss;
    mobileLineItemBottom?: InjectableCss;
    badge?: InjectableCss;
}

// Mobius Icon renders `src` without forwarding props, so the trash-matching stroke is bound here.
const CartLineEditIcon = () => <EditIcon color="#545454" />;

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 0,
        css: css`
            margin-bottom: 0;
            padding: 0;
            position: relative;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;
        `,
    },
    columnContainer: {
        css: css`
            display: grid;
            grid-template-columns: 115px minmax(280px, 340px) 1fr 1fr 1fr 24px;
            gap: 20px;
            width: 100%;
            padding: 16px 0;
            align-items: flex-start;
            border-bottom: 1px solid #dddddd;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-template-columns: 62px 1fr 1fr !important;
                            gap: 12px !important;
                        `,
                        css`
                            grid-template-columns: 70px 270px 1fr 1fr 1fr 21px;
                            gap: 12px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    imageColumn: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 1;
                            grid-column-start: 1;
                            grid-column-end: 2;
                            grid-row-end: 5;
                        `,
                    ],
                    "max",
                )}

            img {
                width: 115px;
                max-width: 115px;
                flex-shrink: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 62px !important;
                                max-width: 62px !important;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    productColumn: {
        css: css`
            display: flex;
            align-items: flex-start;
            gap: 15px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 1;
                            grid-column-start: 2;
                            grid-column-end: 4;
                            gap: 10px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    productDetails: {
        css: css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding-left: 2px;
                            padding-right: 30px;
                        `,
                    ],
                    "max",
                )}

            & a {
                color: ${COLORS.text.main} !important;
                text-decoration: none;
                font-weight: 700;

                span {
                    color: ${COLORS.text.main} !important;
                    text-decoration: none;
                    font-weight: 700;
                }

                &:hover {
                    color: ${COLORS.text.main} !important;
                    text-decoration: underline;
                    font-weight: 700;

                    span {
                        color: ${COLORS.text.main} !important;
                        text-decoration: none;
                        font-weight: 700;
                    }
                }
            }
        `,
    },
    productImage: {
        image: {
            css: css`
                width: 115px !important;
                max-width: 115px !important;
                height: auto;
                object-fit: contain;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 62px !important;
                                max-width: 62px !important;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    badge: {
        css: css`
            margin-top: 8px;
            width: 115px;
            display: flex;
            align-items: center;
            justify-content: flex-start;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 62px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    unitPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            text-align: left;
            font-family: ${TYPOGRAPHY.fontFamily.base};

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 3;
                            grid-column-start: 2;
                            grid-column-end: 3;
                        `,
                    ],
                    "max",
                )}

            legend {
                display: none;
                text-transform: capitalize !important;
                font-weight: 400;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                padding-left: 0;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                display: block;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                color: #494745;
                            `,
                        ],
                        "max",
                    )}
            }

            [class*="InventoryMessage"] {
                span {
                    font-weight: 500;
                    color: #545454;
                }
            }

            span {
                font-weight: 600;
                font-family: ${TYPOGRAPHY.fontFamily.base};
            }
        `,
    },
    qtyColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            justify-content: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 2;
                            grid-column-start: 2;
                            grid-column-end: 4;
                            align-items: flex-start;
                            text-align: left;
                            justify-content: flex-start;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    totalPriceColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 3;
                            grid-column-start: 3;
                            grid-column-end: 4;
                            flex-direction: flex-end;
                            align-items: flex-end;
                            text-align: right;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    customQuantityInput: {
        container: {
            css: css`
                display: flex;
                justify-content: center;
                width: 100%;
            `,
        },
    },
    deleteColumn: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            gap: 12px;
            padding-top: 5px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            position: absolute;
                            top: 16px;
                            right: 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    removeCartLineIcon: {
        src: TrashIcon,
        size: 16,
        color: "text.main",
        css: css`
            &:hover {
                color: ${getColor("danger.main")};
            }
        `,
    },
    editCartLineIcon: {
        src: CartLineEditIcon,
        size: 16,
        color: "text.main",
    },
    productImageGridItem: {
        width: 2,
        css: css`
            > a {
                margin-top: 28px;
                padding-left: 8px;
                > img {
                    /* aspect-ratio: 304 / 384; */
                    width: 100%;
                    height: auto;
                    object-fit: contain;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    css`
                        font-size: 10px;
                    `,
                    null,
                ])}
        `,
    },
    cartLineInfoGridItem: {
        width: [8, 8, 8, 9, 9],
        css: css`
            padding-left: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-left: 0px;
                        flex-basis: 73.666667%;
                        max-width: 73.666667%;
                    `,
                    css`
                        padding-left: 0px;
                        flex-basis: 73.666667%;
                        max-width: 73.666667%;
                    `,
                    css`
                        padding-left: 0px;
                        flex-basis: 73.666667%;
                        max-width: 73.666667%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    cartLineInfoContainer: { gap: 20 },
    productInfoGridItem: { width: [12, 12, 12, 12, 12] },
    rowItems: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding-top: 0;
        `,
    },
    desktopItemsContainer: {
        css: css`
            width: 100%;
        `,
    },
    secondRowItems: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding-top: 9px;
            > div > div {
                margin: 0 !important;
                > section {
                    > legend {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        color: ${COLORS.primary.base};
                        text-transform: none !important;
                    }
                    > div {
                        > span {
                            font-family: ${TYPOGRAPHY.fontFamily.title};
                            font-style: normal;
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            font-size: ${TYPOGRAPHY.size.large};
                            line-height: ${TYPOGRAPHY.lineHeight.medium};
                            color: ${COLORS.primary.base};
                        }
                    }
                }
            }
        `,
    },
    mobileRowItems: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            width: 100%;
            padding-top: 9px;
            padding-left: 10px;
            padding-right: 10px;
            > div > div > div {
                margin: 0 !important;
                > section {
                    > legend {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        color: ${COLORS.primary.base};
                        text-transform: none !important;
                    }
                    > div {
                        > span {
                            font-family: ${TYPOGRAPHY.fontFamily.title};
                            font-style: normal;
                            font-weight: ${TYPOGRAPHY.weight.bold};
                            font-size: ${TYPOGRAPHY.size.large};
                            line-height: ${TYPOGRAPHY.lineHeight.medium};
                            color: ${COLORS.primary.base};
                        }
                    }
                }
            }
        `,
    },
    mobileItemsContainer: {
        css: css`
            width: 100%;
            padding-left: 10px;
            padding-right: 10px;
            padding-bottom: 10px;
        `,
    },
    mobileThirdRowItems: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            padding-top: 18px;
            > div {
                padding-left: 0;
                padding-top: 0;
                padding-bottom: 0;
                max-width: 50%;
                flex-basis: 50%;
                > div > label > button {
                    margin-bottom: 0;
                    margin-top: 0;
                    padding-left: 0;
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.small};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    color: ${getColor("text.link")};
                    /* padding-bottom: 26px; */
                }
            }
            > button {
                flex-basis: 50%;
                max-width: 50%;
                margin-top: 0;
                margin-bottom: 0;
                margin-top: 0;
                padding-left: 0;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.link")};
                /* padding-bottom: 26px; */
            }
        `,
    },
    thirdRowItems: {
        css: css`
            flex-basis: 100%;
            max-width: 100%;
            // padding-top: 18px;
            // border: 2px solid blue;
            > div {
                padding-left: 0;
                padding-top: 0;
                padding-bottom: 0;
                > div > label > button {
                    margin-bottom: 0;
                    margin-top: 0;
                    padding-left: 0;
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.small};
                    line-height: ${TYPOGRAPHY.lineHeight.base};
                    color: ${getColor("text.link")};
                    /* padding-bottom: 26px; */
                }
            }
            > button {
                flex-basis: 50%;
                max-width: 50%;
                margin-top: 0;
                margin-bottom: 0;
                margin-top: 0;
                padding-left: 0;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.small};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${getColor("text.link")};
                /* padding-bottom: 26px; */
            }
        `,
    },
    productInfoContainer: {
        gap: 12,
        css: css`
            padding-top: 28px;
            padding-bottom: 26px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 0;
                    `,
                    css`
                        padding-bottom: 0;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    firstRowItem: {
        css: css`
            display: flex;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-wrap: wrap;
                    `,
                    css`
                        flex-wrap: wrap;
                    `,
                    null,
                    null,
                    null,
                ])}
            > div {
                width: 100%;
                max-width: 100%;
                flex-basis: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            margin-bottom: 10px;
                        `,
                        css`
                            margin-bottom: 10px;
                        `,
                        null,
                        null,
                        null,
                    ])}
                :last-of-type {
                    width: 50%;
                    max-width: 50%;
                    flex-basis: 50%;
                    padding: 0;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                /* justify-content: end; */
                                /* flex-basis: 0%; */
                            `,
                            css`
                                /* justify-content: end; */
                                /* flex-basis: 0%; */
                            `,
                            css`
                                /* justify-content: end; */
                                /* flex-basis: 0%; */
                            `,
                            null,
                            null,
                        ])}
                }
            }
            > div:first-of-type {
                > div:last-of-type {
                    margin-left: 0px;
                    > div > div > div {
                        margin-top: 8px;
                        pointer-events: none;
                        > span {
                            font-size: ${TYPOGRAPHY.size.base};
                            line-height: ${TYPOGRAPHY.lineHeight.base};
                        }
                    }
                }
            }
        `,
    },
    brandAndDescriptionContainer: {
        css: css`
            > a {
                width: auto;
                :first-of-type {
                    > span {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        font-size: ${TYPOGRAPHY.size.base};
                        line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                        color: ${COLORS.primary.base};
                        padding-right: 10px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    font-family: ${TYPOGRAPHY.fontFamily.title};
                                    font-style: normal;
                                    font-weight: ${TYPOGRAPHY.weight.mediumbold};
                                    font-size: ${TYPOGRAPHY.size.larger};
                                    line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                                    color: ${COLORS.primary.base};
                                `,
                                css`
                                    font-family: ${TYPOGRAPHY.fontFamily.title};
                                    font-style: normal;
                                    font-weight: ${TYPOGRAPHY.weight.bold};
                                    font-size: ${TYPOGRAPHY.size.larger};
                                    line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                                    color: ${COLORS.primary.base};
                                `,
                                null,
                                null,
                                null,
                            ])}
                    }
                }
                :last-of-type {
                    > span {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        font-size: ${TYPOGRAPHY.size.larger};
                        line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                        color: ${COLORS.primary.base};
                    }
                }
            }
        `,
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
            padding: 0;
            margin-top: 28px !important;
            margin-top: 0px !important;
            display: inline-block;
            width: 100%;
            max-width: 100%;
            > a {
                display: inline-block;
                width: auto;
                padding-right: 10px;
                > span {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.larger};
                    line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                    color: ${COLORS.primary.base};
                }
                :last-of-type {
                    padding-right: 0px;
                    > span {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        font-size: ${TYPOGRAPHY.size.larger};
                        line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                        color: ${COLORS.primary.base};
                    }
                }
            }
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
        css: css``,
    },
    quantityAndExtendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            padding: 0 !important;
            > div > div > div {
                margin-left: 0;
                padding: 15px;
                padding-top: 8px;
                padding-left: 38px;
                :last-of-type {
                    padding-bottom: 0;
                }
                > div > div:last-of-type {
                    + section {
                        padding-left: 38px;
                        padding-top: 15px;
                    }
                }
            }
        `,
    },
    mobileQuantityAndExtendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            padding: 0 !important;
            flex-basis: 50%;
            max-width: 50%;
            > div > div > div {
                margin-left: 0;
                padding: 15px;
                padding-top: 8px;
                padding-left: 38px;
                :last-of-type {
                    padding-bottom: 0;
                }
                > div > div:last-of-type {
                    + section {
                        padding-left: 38px;
                        padding-top: 15px;
                    }
                }
            }
        `,
    },
    quantityAndExtendedUnitNetPriceContainer: {
        css: css`
            > div {
                margin: 0 !important;
                > section {
                    > legend {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                        color: ${COLORS.primary.base};
                        text-transform: none !important;
                    }

                    > div > span:first-of-type {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-style: normal;
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        font-size: ${TYPOGRAPHY.size.large};
                        line-height: ${TYPOGRAPHY.lineHeight.medium};
                        color: ${COLORS.primary.base};
                    }
                }
            }
        `,
    },
    quantityGridItem: {
        width: 6,
        css: css`
            display: flex;
            flex-basis: 100%;
            max-width: 100%;
            > div {
                display: flex;
                flex-basis: 50%;
                max-width: 50%;
                flex-direction: row;
                > label {
                    padding-top: 10px;
                    /* text-align: center; */
                    padding-bottom: 10px;
                    min-width: 100%;
                    min-width: auto;
                    word-break: keep-all;
                    padding-right: 10px;

                    padding: 0;
                    display: flex;
                    align-items: center;
                    width: auto;
                    margin-right: 10px;
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-style: normal;
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    font-size: ${TYPOGRAPHY.size.base};
                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                    color: ${COLORS.primary.base} !important;
                    text-transform: none !important;
                }
                > div:last-of-type {
                    margin-top: 0px !important;
                    max-width: 100%;
                    min-width: 65px;
                    width: auto;
                    > input {
                        max-width: none;
                        width: auto;
                        min-width: 65px;
                        background-color: ${getColor("secondary")} !important;
                        height: 44px !important;
                        padding-left: 4px;
                        padding-right: 4px;
                        padding-top: 10px;
                        padding-bottom: 10px;
                        text-align: center;
                        color: ${COLORS.primary.base} !important;
                        border: 1px solid ${COLORS.primary.base};
                    }
                }
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
            padding: 0;
            > legend {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.base};
                color: ${COLORS.primary.base};
                text-transform: none !important;
            }
            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-style: normal;
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.large};
                line-height: ${TYPOGRAPHY.lineHeight.medium};
                color: ${COLORS.primary.base};
            }
        `,
    },
    priceLabelText: {
        variant: "legend",
    },
    extendedUnitNetPriceText: {
        weight: "bold",
        css: css`white-space: nowrap;`,
    },
    vatLabelText: {
        size: 12,
        css: css`
            color: #545454;
        `,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
            > div:first-of-type {
                padding-left: 0px;
                padding-right: 0px;
                padding-top: 28px;
            }
            > section {
                padding-top: 14px;
            }
            > button:last-of-type {
                margin-top: 24px;
                margin-bottom: 26px;
            }
        `,
    },
    addToListLink: {
        css: css`
            margin-top: 10px;
            padding-left: 0;

            span {
                white-space: nowrap;
                color: #a45d44 !important;
            }

            &,
            &:hover,
            &:hover span {
                color: #a45d44 !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-top: 0;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: 10px;
        `,
    },
    cartLineNotesGridItem: {
        width: 12,
    },
    cartLineServiceGridItem: { width: 12 },
    serviceLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: italic;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: #545454;
        `,
    },
    removeCartLineGridItem: {
        css: css`
            justify-content: center;
            flex-basis: 6.666667% !important;
            max-width: 6.666667% !important;
            > button {
                padding-top: 20px;
            }
        `,
        width: [2, 2, 2, 1, 1],
    },
    binInformationsGridItem: {
        width: 12,
    },
    modalContainer: {
        size: 450,
        cssOverrides: {
            modalTitle: css`
                border: 0px;
                padding: 10px 20px;
                background-color: #f8f8f8;
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-weight: ${TYPOGRAPHY.weight.bold};
                font-size: ${TYPOGRAPHY.size.larger};
                line-height: ${TYPOGRAPHY.lineHeight.lmedium};
                font-style: normal;

                h1,
                h2,
                h3,
                h4,
                h5,
                h6 {
                    font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    font-size: ${TYPOGRAPHY.size.larger} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.lmedium} !important;
                    font-style: normal;
                }
            `,
            modalContent: css`
                padding: 15px 20px;
                font-family: ${TYPOGRAPHY.fontFamily.title} !important;
            `,
            modalBody: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 95%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    modalBodyContainer: {
        css: css`
            display: flex;
            padding: 16px;
            margin-top: 15px;
        `,
        offsetCss: css`
            justify-content: right;
        `,
    },
    closeButton: {
        css: css`
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    modalYesButton: {
        buttonType: "outline",
        css: css`
            margin-right: 10px;
            span[class^="TypographyStyle"] {
                color: black;
                font-size: 14px;
            }
        `,
    },
    modalContent: {
        css: css`
            line-height: 24px;
            font-size: 15px;
            font-weight: 400;
            font-family: Mulish;
        `,
    },
    mobileLineItemBottomContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            grid-row-start: 4;
                            grid-column-start: 2;
                            grid-column-end: 4;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    mobileLineItemBottom: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    configurationOptionText: {
        css: css`
            display: block;
        `,
    },
    mgbwShippingAvailabilityText: {
        css: css`
            display: block;
            margin-top: 8px;
            line-height: 1.4;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: 500 !important;
            color: #545454;
        `,
    },
    configurationDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    configurationSummary: {
        color: "#545454",
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 1.5;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            font-size: ${TYPOGRAPHY.size.smaller};
                        `,
                    ],
                    "max",
                )}
        `,
    },
    configurationLabel: {
        color: "#545454",
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
            text-transform: capitalize;
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showPricing,
    showVmiBinDetails,
    suryaWebsiteUrl,
    globalViewsWebsiteUrl,
    mgbwWebsiteUrl,
}) => {
    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);
    const [isOpen, setIsOpen] = useState(false);

    // Parse MGBW configurator selections from cart line properties
    const mgbwSelections = useMgbwSelections(cartLine.properties);
    const mgbwConfigureUrl = useMemo(() => {
        // Redirect to the MGBW store PDP when the line is viewed on Surya / Global Views.
        const basePath =
            buildCrossWebsiteProductDetailPath(cartLine.productUri, cartLine.properties?.sourceBrand, suryaWebsiteUrl, globalViewsWebsiteUrl, mgbwWebsiteUrl) ??
            cartLine.productUri;
        const separator = basePath.includes("?") ? "&" : "?";
        const qty = cartLine.qtyOrdered ?? 1;
        return `${basePath}${separator}${MGBW_UPDATE_CART_LINE_ID}=${encodeURIComponent(cartLine.id)}&${MGBW_UPDATE_QTY}=${qty}`;
    }, [cartLine.productUri, cartLine.id, cartLine.qtyOrdered, cartLine.properties?.sourceBrand, suryaWebsiteUrl, globalViewsWebsiteUrl, mgbwWebsiteUrl]);

    // The configurator only restores selections from the URL, so an edit link has to carry them.
    const mgbwEditUrl = useMemo(
        () => appendSelectionsToUrl(mgbwConfigureUrl, getMgbwSelectionsRecord(cartLine.properties)),
        [mgbwConfigureUrl, cartLine.properties],
    );

    const visibleSelections = useVisibleMgbwSelections(cartLine.properties);

    const configurationItemNumber = cartLine.properties?.[MGBW_CONFIGURATION_ITEM_NUMBER] || "";

    const mgbwShippingAvailability = cartLine.properties?.[MGBW_SHIPPING_AVAILABILITY] || "";

    const isInvalidConfiguration = cartLine.properties?.[MGBW_VALIDATION_STATUS] === MgbwValidationStatus.stale;

    const validationMessages = React.useMemo(() => {
        const raw = cartLine.properties?.[MGBW_VALIDATION_MESSAGES];
        if (!raw) {
            return [] as string[];
        }
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed.filter((message): message is string => typeof message === "string") : [];
        } catch {
            return [raw];
        }
    }, [cartLine.properties?.[MGBW_VALIDATION_MESSAGES]]);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLinesHandler();
            } else {
                const linkedRugPadCartLine = cart.cartLines?.find(
                    line => line.properties.isRollpadService === "true" && line.properties.linkedRugItemCode === cartLine.productName,
                );
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });

                // If linked rug pad quantity exceeds new rug quantity,
                // reduce rug pad quantity to match the rug.
                if (linkedRugPadCartLine && (linkedRugPadCartLine.qtyOrdered ?? 0) > qtyOrdered) {
                    updateCartLine({
                        cartLine: {
                            ...linkedRugPadCartLine,
                            qtyOrdered,
                        },
                    });
                }
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
                shouldNotLoadFullCart: true,
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Side Marks Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction) {
            return;
        }

        const isRugLinked = cart.cartLines?.some(x => x.properties.linkedRugItemCode === cartLine.productName);
        if (isRugLinked || cartLine.properties.isRollpadService === "true") {
            setIsOpen(true);
        } else {
            removeCartLinesHandler();
        }
    };

    const removeCartLinesHandler = () => {
        const itemsToRemove: string[] = [];
        itemsToRemove.push(cartLine.id);

        cart.cartLines?.forEach(element => {
            if (element.properties.isRollpadService === "true" && element.properties.linkedRugItemCode === cartLine.productName) {
                itemsToRemove.push(element.id);
            }
        });
        removeCartLine({ cartLineIds: itemsToRemove });
    };

    const deleteCartHandler = () => {
        setIsOpen(false);
        removeCartLinesHandler();
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo: AddToWishListParameter["productInfos"][number] & { properties?: Record<string, string> } = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
            properties: { ...(cartLine.properties || {}) },
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine({
            cartLine: {
                ...cartLine,
                properties: {
                    productSubscription: JSON.stringify(updatedSubscription),
                },
            },
        });
    };

    const sourceBrandNormalized = (cartLine?.properties?.sourceBrand ?? "").toLowerCase().replace(/\s+/g, "");
    const isMgbwSourceBrand = sourceBrandNormalized === "mg+bw" || sourceBrandNormalized === "mgbw" || sourceBrandNormalized === "mitchellgold+bobwilliams";
    const errorMessages = getErrorMessages(cart, cartLine, showInventoryAvailability);
    const isMgbwUnconfigured = cartLine.properties?.[MGBW_IS_UNCONFIGURED] === "true";
    // MGBW configurable product = has configurator data on the cart line.
    // Case goods (e.g. 10712-STB) carry no MGBW configurator properties and are excluded here,
    // so the brand-hiding / aligned detail layout only applies to configurable MGBW products.
    const isMgbwConfigurable = isMgbwSourceBrand && (mgbwSelections.length > 0 || isMgbwUnconfigured || !!configurationItemNumber);
    // Unconfigured / stale lines start the configurator fresh; valid ones reopen with their current selections.
    const mgbwEditHref = isMgbwUnconfigured || isInvalidConfiguration ? mgbwConfigureUrl : mgbwEditUrl;

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    return (
        <GridContainer {...styles.container} data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}>
            <StyledWrapper {...styles.columnContainer}>
                <StyledWrapper {...styles.imageColumn}>
                    {showImage && <ProductImage product={cartLine} extendedStyles={styles.productImage} />}
                    <StyledWrapper {...styles.badge}>
                        <BrandBadge brandName={cartLine?.properties?.sourceBrand} />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.productColumn}>
                    <StyledWrapper {...styles.productDetails}>
                        {/* Brand */}
                        {showBrand && cartLine.brand && sourceBrandNormalized !== "globalviews" && !isMgbwSourceBrand && (
                            <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                        )}

                        <StyledWrapper
                            css={css`
                                display: flex;
                                justify-content: space-between;
                                align-items: flex-start;
                            `}
                        >
                            {showProductTitle && (
                                <ProductDescription
                                    product={cartLine}
                                    extendedStyles={styles.productDescription}
                                    showSecondaryDetails={isMgbwSourceBrand && !isMgbwConfigurable}
                                />
                            )}
                            <Hidden above="sm">
                                {/* Delete Column */}
                                <StyledWrapper {...styles.deleteColumn}>
                                    {isMgbwConfigurable && (
                                        <Clickable
                                            href={mgbwEditHref}
                                            title={translate("Edit configuration")}
                                            data-test-selector="cartline_editConfiguration"
                                        >
                                            <VisuallyHidden>{translate("Edit configuration")}</VisuallyHidden>
                                            <IconMemo {...styles.editCartLineIcon} />
                                        </Clickable>
                                    )}
                                    {showRemoveAction && (
                                        <Clickable
                                            onClick={removeCartLineClickHandler}
                                            title={translate("Remove item")}
                                            data-test-selector="cartline_removeLine"
                                        >
                                            <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                            <IconMemo {...styles.removeCartLineIcon} />
                                        </Clickable>
                                    )}
                                </StyledWrapper>
                            </Hidden>
                        </StyledWrapper>

                        {/* Configuration selections / sidemarks */}
                        {isMgbwUnconfigured ? (
                            <StyledWrapper
                                css={css`
                                    display: flex;
                                    flex-direction: column;
                                    align-items: flex-start;
                                    gap: 4px;
                                `}
                            >
                                <Typography
                                    {...styles.configurationOptionText}
                                    css={css`
                                        color: #c44b25;
                                        font-weight: ${TYPOGRAPHY.weight.baseBold};
                                    `}
                                >
                                    {translate("Not Configured")}
                                </Typography>
                                <Link {...mgbwConfiguratorLinkStyles} href={mgbwConfigureUrl}>
                                    {translate("Configure")}
                                </Link>
                            </StyledWrapper>
                        ) : visibleSelections.length > 0 ? (
                            <StyledWrapper {...styles.configurationDetails}>
                                {visibleSelections.map(entry => (
                                    <Typography {...styles.configurationSummary} key={entry.OptionId}>
                                        <Typography {...styles.configurationLabel} as="span">
                                            {entry.Name}:
                                        </Typography>{" "}
                                        {entry.Value}
                                    </Typography>
                                ))}
                                {isInvalidConfiguration && (
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            gap: 4px;
                                            margin-top: 8px;
                                        `}
                                    >
                                        <Typography
                                            {...styles.configurationOptionText}
                                            css={css`
                                                color: #c44b25;
                                                font-weight: ${TYPOGRAPHY.weight.baseBold};
                                            `}
                                        >
                                            {validationMessages[0] ?? translate("This configuration is no longer available.")}
                                        </Typography>
                                        <Link {...mgbwConfiguratorLinkStyles} href={mgbwConfigureUrl}>
                                            {translate("Configure")}
                                        </Link>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        ) : (
                            !cartLine.isFixedConfiguration &&
                            cartLine.sectionOptions!.length > 0 && (
                                <StyledWrapper>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}: ${option.optionName}`}
                                        </Typography>
                                    ))}
                                </StyledWrapper>
                            )
                        )}

                        {/* Shipping availability (option-dependent) - mobile only; desktop renders under unit price */}
                        {mgbwShippingAvailability && (
                            <Hidden above="sm">
                                <StyledWrapper
                                    css={css`
                                        margin-top: 8px;
                                    `}
                                >
                                    <Typography
                                        css={css`
                                            display: block;
                                            font-size: ${TYPOGRAPHY.size.smaller};
                                        `}
                                    >
                                        {mgbwShippingAvailability}
                                    </Typography>
                                </StyledWrapper>
                            </Hidden>
                        )}

                        <Hidden above="sm">
                            {/* In Stock under unit price */}
                            {showInventoryAvailability && !cartLine.quoteRequired && !mgbwShippingAvailability && (
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    isSwatch={cartLine.properties?.mgbw_isSwatch === "true"}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                    extendedStyles={styles.productAvailability}
                                />
                            )}
                        </Hidden>

                        {/* Error messages */}
                        {errorMessages.length > 0 && (
                            <StyledWrapper>
                                {errorMessages.map((message, index) => (
                                    <Typography {...styles.cartLineErrorMessageText} key={index} data-test-selector="cartline_errorMessage">
                                        {message}
                                    </Typography>
                                ))}
                            </StyledWrapper>
                        )}
                        {/* Additional Information Sections */}
                        <Hidden below="md">
                            {(showLineNotes ||
                                showAddToList ||
                                cartLine.isSubscription ||
                                cart.showCostCode ||
                                cartLine.properties.isRollpadService === "true") && (
                                <StyledWrapper>
                                    {/* Cost Code Section */}
                                    {cart.showCostCode && !cartLine.isPromotionItem && (
                                        <GridContainer
                                            css={css`
                                                margin-bottom: 10px;
                                            `}
                                        >
                                            <GridItem width={12}>
                                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                                                {cart.canEditCostCode ? (
                                                    <Select
                                                        value={cartLine.costCode}
                                                        onChange={costCodeChangeHandler}
                                                        aria-label={translate("Cost Code")}
                                                        {...styles.costCodeSelect}
                                                    >
                                                        {cart.costCodes?.map(costCode => (
                                                            <option key={costCode.costCode} value={costCode.costCode}>
                                                                {costCode.description}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                                                )}
                                            </GridItem>
                                        </GridContainer>
                                    )}

                                    {/* Roll Pad Service */}
                                    {cartLine.properties.isRollpadService === "true" && (
                                        <StyledWrapper
                                            {...styles.serviceLabel}
                                            css={css`
                                                ${styles.serviceLabel?.css}
                                                margin-bottom: 10px;
                                            `}
                                        >
                                            Roll Pad Service
                                        </StyledWrapper>
                                    )}

                                    {/* Line Notes */}
                                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                                        <StyledWrapper
                                            css={css`
                                                margin: 10px 0;
                                            `}
                                        >
                                            <CartLineNotes
                                                cart={cart}
                                                editable={cartLine.isActive}
                                                onNotesChange={notesChangeHandler}
                                                extendedStyles={styles.cartLineNotes}
                                            />
                                        </StyledWrapper>
                                    )}

                                    {/* Action Links */}
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            gap: 15px;
                                            align-items: center;
                                        `}
                                    >
                                        {cartLine.isSubscription && cartLine.productSubscription && (
                                            <ProductDeliveryScheduleButton
                                                subscription={
                                                    cartLine.properties.productSubscription
                                                        ? JSON.parse(cartLine.properties.productSubscription)
                                                        : cartLine.productSubscription
                                                }
                                                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || !cartLine.qtyOrdered || cartLine.qtyOrdered <= 0}
                                                onSave={saveScheduleHandler}
                                                extendedStyles={styles.deliveryScheduleButton}
                                            />
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                        </Hidden>
                    </StyledWrapper>
                </StyledWrapper>

                {/* Unit Price Column */}
                <StyledWrapper {...styles.unitPriceColumn}>
                    {isMgbwUnconfigured ? (
                        <Typography
                            css={css`
                                color: #888;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                font-style: italic;
                            `}
                        >
                            {translate("Configure to see price")}
                        </Typography>
                    ) : (
                        <>
                            {!cart.cartNotPriced && (
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    showInvalidPriceMessage={true}
                                    extendedStyles={styles.productPrice}
                                    showLabel={true}
                                    showUnitOfMeasure={showPerUnitPrice}
                                />
                            )}
                        </>
                    )}

                    <Hidden below="md">
                        {/* MGBW shipping availability under unit price (configurable products) */}
                        {mgbwShippingAvailability && <Typography {...styles.mgbwShippingAvailabilityText}>{mgbwShippingAvailability}</Typography>}
                        {/* In Stock under unit price */}
                        {showInventoryAvailability && !cartLine.quoteRequired && !mgbwShippingAvailability && (
                            <ProductAvailability
                                productId={cartLine.productId!}
                                availability={cartLine.availability!}
                                unitOfMeasure={cartLine.unitOfMeasure}
                                trackInventory={cartLine.trackInventory}
                                isSwatch={cartLine.properties?.mgbw_isSwatch === "true"}
                                failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                extendedStyles={styles.productAvailability}
                            />
                        )}
                    </Hidden>

                    <Hidden below="md">
                        {/* Add to Project under in stock */}
                        {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                            <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                {translate("Add to Project")}
                            </Link>
                        )}
                    </Hidden>
                </StyledWrapper>

                {/* Quantity Column */}
                <StyledWrapper {...styles.qtyColumn}>
                    <CustomQuantityInput
                        cartLine={cartLine}
                        cart={cart}
                        canModifyOrder={cart.canModifyOrder || false}
                        isLoading={false}
                        cartLoadedTime={Date.now()}
                        onQtyOrderedChange={qtyOrderedChangeHandler}
                        extendedStyles={styles.customQuantityInput}
                    />
                </StyledWrapper>

                {/* Total Price Column */}
                <StyledWrapper {...styles.totalPriceColumn}>
                    {isMgbwUnconfigured ? (
                        <Typography
                            css={css`
                                color: #888;
                                font-size: ${TYPOGRAPHY.size.smaller};
                                font-style: italic;
                            `}
                        >
                            {translate("--")}
                        </Typography>
                    ) : (
                        !cartLine.quoteRequired &&
                        !cart.cartNotPriced &&
                        cartLine.pricing &&
                        showPricing && (
                            <>
                                <Hidden above="sm">
                                    <Typography
                                        css={css`
                                            color: #494745;
                                            font-size: ${TYPOGRAPHY.size.smaller};
                                        `}
                                    >
                                        Total
                                    </Typography>
                                </Hidden>
                                <Typography {...styles.extendedUnitNetPriceText} data-test-selector="cartline_extendedUnitNetPrice">
                                    {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                        ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                        : cartLine.pricing.extendedUnitNetPriceDisplay}
                                </Typography>
                                {enableVat && (
                                    <>
                                        <Typography as="p" {...styles.vatLabelText}>
                                            {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                : translate("Ex. VAT")}
                                        </Typography>
                                        {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <Typography {...styles.subtotalWithoutVatText}>{cartLine.pricing.extendedUnitNetPriceDisplay}</Typography>
                                                <Typography as="p" {...styles.vatLabelText}>
                                                    {translate("Ex. VAT")}
                                                </Typography>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )
                    )}
                </StyledWrapper>

                <Hidden below="md">
                    {/* Delete Column */}
                    <StyledWrapper {...styles.deleteColumn}>
                        {isMgbwConfigurable && (
                            <Clickable href={mgbwEditHref} title={translate("Edit configuration")} data-test-selector="cartline_editConfiguration">
                                <VisuallyHidden>{translate("Edit configuration")}</VisuallyHidden>
                                <IconMemo {...styles.editCartLineIcon} />
                            </Clickable>
                        )}
                        {showRemoveAction && (
                            <Clickable onClick={removeCartLineClickHandler} title={translate("Remove item")} data-test-selector="cartline_removeLine">
                                <VisuallyHidden>{translate("Remove item")}</VisuallyHidden>
                                <IconMemo {...styles.removeCartLineIcon} />
                            </Clickable>
                        )}
                    </StyledWrapper>
                </Hidden>

                <Hidden above="sm" {...styles.mobileLineItemBottomContainer}>
                    <StyledWrapper {...styles.mobileLineItemBottom}>
                        {/* Additional Information Sections */}
                        {(showLineNotes ||
                            showAddToList ||
                            cartLine.isSubscription ||
                            cart.showCostCode ||
                            cartLine.properties.isRollpadService === "true") && (
                            <StyledWrapper>
                                {/* Cost Code Section */}
                                {cart.showCostCode && !cartLine.isPromotionItem && (
                                    <GridContainer
                                        css={css`
                                            margin-bottom: 10px;
                                        `}
                                    >
                                        <GridItem width={12}>
                                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                                            {cart.canEditCostCode ? (
                                                <Select
                                                    value={cartLine.costCode}
                                                    onChange={costCodeChangeHandler}
                                                    aria-label={translate("Cost Code")}
                                                    {...styles.costCodeSelect}
                                                >
                                                    {cart.costCodes?.map(costCode => (
                                                        <option key={costCode.costCode} value={costCode.costCode}>
                                                            {costCode.description}
                                                        </option>
                                                    ))}
                                                </Select>
                                            ) : (
                                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                                            )}
                                        </GridItem>
                                    </GridContainer>
                                )}

                                {/* Roll Pad Service */}
                                {cartLine.properties.isRollpadService === "true" && (
                                    <StyledWrapper
                                        {...styles.serviceLabel}
                                        css={css`
                                            ${styles.serviceLabel?.css}
                                            margin-bottom: 10px;
                                        `}
                                    >
                                        Roll Pad Service
                                    </StyledWrapper>
                                )}

                                {/* Line Notes */}
                                {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                                    <StyledWrapper>
                                        <CartLineNotes
                                            cart={cart}
                                            editable={cartLine.isActive}
                                            onNotesChange={notesChangeHandler}
                                            extendedStyles={styles.cartLineNotes}
                                        />
                                    </StyledWrapper>
                                )}

                                {/* Action Links */}
                                <StyledWrapper
                                    css={css`
                                        display: flex;
                                        gap: 15px;
                                        align-items: center;
                                    `}
                                >
                                    {cartLine.isSubscription && cartLine.productSubscription && (
                                        <ProductDeliveryScheduleButton
                                            subscription={
                                                cartLine.properties.productSubscription
                                                    ? JSON.parse(cartLine.properties.productSubscription)
                                                    : cartLine.productSubscription
                                            }
                                            disabled={!cart.canModifyOrder || cartLine.isPromotionItem || !cartLine.qtyOrdered || cartLine.qtyOrdered <= 0}
                                            onSave={saveScheduleHandler}
                                            extendedStyles={styles.deliveryScheduleButton}
                                        />
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                        {/* Add to Project under in stock */}
                        {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                            <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                {translate("Add to Project")}
                            </Link>
                        )}
                    </StyledWrapper>
                </Hidden>
            </StyledWrapper>

            {/* Modal for confirmation */}
            <Modal {...styles.modalContainer} isOpen={isOpen} isCloseable={false} sizeVariant="small" headline={translate("Alert!")}>
                <Typography {...styles.modalContent}>{siteMessage("Rugpad_DeleteConfirmation")}</Typography>
                <GridContainer {...styles.modalBodyContainer}>
                    <Button {...styles.modalYesButton} onClick={deleteCartHandler}>
                        Yes
                    </Button>
                    <Button {...styles.closeButton} onClick={() => setIsOpen(false)}>
                        {siteMessage("Cancel")}
                    </Button>
                </GridContainer>
            </Modal>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
